import { useState } from "react";
import { useMFA } from "@/hooks/useMFA";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import {
  Shield,
  Smartphone,
  Key,
  CheckCircle,
  AlertTriangle,
  Loader2,
  Copy,
  RefreshCw,
  Trash2,
} from "lucide-react";

type EnrollmentStep = 'intro' | 'qrcode' | 'verify' | 'success';

interface TOTPEnrollmentData {
  id: string;
  qr_code: string;
  secret: string;
}

export const MFAEnrollment = () => {
  const { toast } = useToast();
  const {
    isEnrolled,
    isVerified,
    factors,
    isLoading,
    error,
    enrollTOTP,
    verifyTOTP,
    unenrollFactor,
    refreshStatus,
    clearError,
  } = useMFA();

  const [step, setStep] = useState<EnrollmentStep>('intro');
  const [enrollmentData, setEnrollmentData] = useState<TOTPEnrollmentData | null>(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  const handleStartEnrollment = async () => {
    setIsProcessing(true);
    clearError();

    const data = await enrollTOTP('CastReady Admin');

    if (data) {
      setEnrollmentData({
        id: data.id,
        qr_code: data.totp.qr_code,
        secret: data.totp.secret,
      });
      setStep('qrcode');
    }

    setIsProcessing(false);
  };

  const handleVerifyCode = async () => {
    if (!enrollmentData || verificationCode.length !== 6) return;

    setIsProcessing(true);
    clearError();

    const success = await verifyTOTP(enrollmentData.id, verificationCode);

    if (success) {
      setStep('success');
      toast({
        title: "MFA Enabled",
        description: "Two-factor authentication has been successfully enabled for your account.",
      });
    }

    setIsProcessing(false);
  };

  const handleUnenroll = async (factorId: string) => {
    if (!confirm("Are you sure you want to disable MFA? This will make your account less secure.")) {
      return;
    }

    setIsProcessing(true);
    const success = await unenrollFactor(factorId);

    if (success) {
      toast({
        title: "MFA Disabled",
        description: "Two-factor authentication has been disabled.",
        variant: "destructive",
      });
      setStep('intro');
      setEnrollmentData(null);
    }

    setIsProcessing(false);
  };

  const copySecret = () => {
    if (enrollmentData?.secret) {
      navigator.clipboard.writeText(enrollmentData.secret);
      toast({
        title: "Copied",
        description: "Secret key copied to clipboard",
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Already enrolled - show status
  if (isEnrolled && step !== 'success') {
    const verifiedFactor = factors.find(f => f.status === 'verified');

    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-green-600" />
            Two-Factor Authentication
          </CardTitle>
          <CardDescription>
            Your account is protected with MFA
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert className="border-green-200 bg-green-50">
            <CheckCircle className="h-4 w-4 text-green-600" />
            <AlertTitle className="text-green-800">MFA Enabled</AlertTitle>
            <AlertDescription className="text-green-700">
              Your account is secured with two-factor authentication.
            </AlertDescription>
          </Alert>

          <div className="space-y-3">
            <h4 className="font-medium text-sm">Enrolled Authenticators</h4>
            {factors.filter(f => f.status === 'verified').map((factor) => (
              <div
                key={factor.id}
                className="flex items-center justify-between p-3 border rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <Smartphone className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <p className="font-medium text-sm">
                      {factor.friendly_name || 'Authenticator App'}
                    </p>
                    <Badge variant="outline" className="text-xs">
                      TOTP
                    </Badge>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleUnenroll(factor.id)}
                  disabled={isProcessing}
                >
                  <Trash2 className="w-4 h-4 text-destructive" />
                </Button>
              </div>
            ))}
          </div>

          <Button
            variant="outline"
            onClick={refreshStatus}
            className="w-full"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh Status
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-primary" />
          Two-Factor Authentication
        </CardTitle>
        <CardDescription>
          Secure your admin account with an authenticator app
        </CardDescription>
      </CardHeader>
      <CardContent>
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {step === 'intro' && (
          <div className="space-y-4">
            <Alert>
              <Key className="h-4 w-4" />
              <AlertTitle>Required for Admin Access</AlertTitle>
              <AlertDescription>
                Two-factor authentication is mandatory for all admin accounts.
                You'll need an authenticator app like Google Authenticator,
                Authy, or 1Password.
              </AlertDescription>
            </Alert>

            <div className="bg-muted/50 rounded-lg p-4 space-y-2">
              <h4 className="font-medium text-sm">Compatible Apps:</h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Google Authenticator (iOS/Android)</li>
                <li>• Authy (iOS/Android/Desktop)</li>
                <li>• 1Password</li>
                <li>• Microsoft Authenticator</li>
              </ul>
            </div>

            <Button
              onClick={handleStartEnrollment}
              disabled={isProcessing}
              className="w-full"
            >
              {isProcessing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Smartphone className="w-4 h-4 mr-2" />
              )}
              Set Up Authenticator App
            </Button>
          </div>
        )}

        {step === 'qrcode' && enrollmentData && (
          <div className="space-y-4">
            <div className="text-center">
              <p className="text-sm text-muted-foreground mb-4">
                Scan this QR code with your authenticator app
              </p>

              <div className="bg-white p-4 rounded-lg inline-block border">
                <img
                  src={enrollmentData.qr_code}
                  alt="MFA QR Code"
                  className="w-48 h-48"
                />
              </div>
            </div>

            <div className="text-center">
              <p className="text-xs text-muted-foreground mb-2">
                Or enter this code manually:
              </p>
              <div className="flex items-center justify-center gap-2">
                <code className="bg-muted px-3 py-1 rounded text-sm font-mono">
                  {enrollmentData.secret}
                </code>
                <Button variant="ghost" size="sm" onClick={copySecret}>
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="verification-code">Verification Code</Label>
              <Input
                id="verification-code"
                placeholder="Enter 6-digit code"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                maxLength={6}
                className="text-center text-lg tracking-widest font-mono"
              />
              <p className="text-xs text-muted-foreground">
                Enter the 6-digit code from your authenticator app
              </p>
            </div>

            <Button
              onClick={handleVerifyCode}
              disabled={isProcessing || verificationCode.length !== 6}
              className="w-full"
            >
              {isProcessing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle className="w-4 h-4 mr-2" />
              )}
              Verify & Enable MFA
            </Button>

            <Button
              variant="ghost"
              onClick={() => {
                setStep('intro');
                setEnrollmentData(null);
                setVerificationCode('');
              }}
              className="w-full"
            >
              Cancel
            </Button>
          </div>
        )}

        {step === 'success' && (
          <div className="space-y-4 text-center">
            <div className="flex justify-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-lg">MFA Enabled Successfully!</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Your admin account is now protected with two-factor authentication.
              </p>
            </div>

            <Alert className="border-amber-200 bg-amber-50">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <AlertTitle className="text-amber-800">Important</AlertTitle>
              <AlertDescription className="text-amber-700">
                Keep your authenticator app safe. If you lose access, you'll need
                to contact a super admin to reset your MFA.
              </AlertDescription>
            </Alert>

            <Button
              onClick={() => window.location.reload()}
              className="w-full"
            >
              Continue to Admin Portal
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
